import {useCallback, useState, useRef, useEffect} from "react";
import axios, { AxiosError } from "axios";

// Api
import {
    SendSupportRequest,
    SupportRequestResult,
} from "../../Api/Brevo";

// Models
import SupportRequestPayload from "../../Models/DTOs/SupportRequestPayloadDTO";

type UseSendSupportRequestResult = {
    sendSupportRequest: (payload: SupportRequestPayload) => Promise<void>;
    isSending: boolean;
    isSuccess: boolean;
    error: string | null;
};

function extractErrorMessage(err: AxiosError): string {
    const status = err.response?.status;
    const data = err.response?.data;

    // No response at all (network error, CORS, DNS, etc.)
    if (!err.response) {
        return "We couldn’t reach the server. Please check your connection and try again.";
    }

    // Explicit special-case: 502 from Nginx
    if (status === 502) {
        return "502 Bad Gateway";
    }

    // Any 5xx – server-side issue, ignore HTML padding
    if (status && status >= 500) {
        return `Server error (${status}). Please try again later.`;
    }

    // If backend sends a useful message, prefer that – but avoid HTML blobs
    if (typeof data === "string" && data.trim() !== "") {
        const trimmed = data.trim();

        // If it looks like HTML (Nginx pages, ASP.NET, etc.), skip it
        if (trimmed.startsWith("<")) {
            return status
                ? `Request failed (${status}).`
                : "Request failed.";
        }

        return trimmed;
    }

    if (data && typeof data === "object") {
        const anyData = data as any;
        if (typeof anyData.error === "string") return anyData.error;
        if (typeof anyData.message === "string") return anyData.message;
    }

    if (err.message) return err.message;

    return "Failed to send message.";
}

export default function useSendSupportRequest(): UseSendSupportRequestResult {
    const
        [isSending, setIsSending] = useState(false),
        [isSuccess, setIsSuccess] = useState(false),
        [error, setError] = useState<string | null>(null),

        abortRef = useRef<AbortController | null>(null),

        sendSupportRequest = useCallback(
            async (payload: SupportRequestPayload) => {
                setIsSending(true);
                setIsSuccess(false);
                setError(null);

                const abortController = new AbortController();
                abortRef.current = abortController;

                try {
                    const result: SupportRequestResult = await SendSupportRequest(
                        payload,
                        abortController,
                    );

                    if (axios.isAxiosError(result)) {
                        const msg = extractErrorMessage(result);
                        setError(msg);
                        throw result; // let the caller know it failed if they care
                    }

                    setIsSuccess(true);
                } finally {
                    setIsSending(false);
                    abortRef.current = null;
                }
            },
            [],
        );

    useEffect(() => {
        return () => {
            if (abortRef.current) {
                abortRef.current.abort();
            }
        };
    }, []);

    return {sendSupportRequest, isSending, isSuccess, error};
}
